// lib/quote-doc/translations.mjs — TICKET-933 (bilingual quote content).
//
// The text columns of a quote row are the SOURCE text, in the language it was
// written in. `row.translations[<lang>]` holds the other-language copy, stamped
// with `_src` = a hash of the source text it was made from. Source edited -> hash
// differs -> that copy is STALE and is redone on the next translate. PURE module.

export const TRANSLATION_LANGS = Object.freeze(['en', 'es']);

const LANG_MARKERS = Object.freeze({
  es: new Set(['de', 'del', 'la', 'las', 'el', 'los', 'y', 'para', 'con', 'por', 'una', 'un', 'en', 'sin', 'muestras', 'fundas', 'toldo', 'cojines', 'gestión', 'consultoría', 'directrices', 'diseño', 'sistemas', 'unión', 'espuma', 'policarbonato']),
  en: new Set(['the', 'and', 'for', 'with', 'of', 'to', 'without', 'samples', 'covers', 'cushions', 'management', 'consulting', 'guidelines', 'design', 'systems', 'joining', 'foam', 'polycarbonate', 'phase']),
});

/**
 * Best-effort language of a short customer-facing text: 'en' | 'es' | null
 * (null = too short or too mixed to call — never a guess). PURE.
 * @param {string} str
 */
export function detectTextLanguage(str) {
  const s = String(str || '').toLowerCase();
  const words = s.match(/[a-záéíóúüñ]+/g) || [];
  let es = /[áéíóúñ¿¡]/.test(s) ? 2 : 0;
  let en = 0;
  for (const w of words) {
    if (LANG_MARKERS.es.has(w)) es += 1;
    if (LANG_MARKERS.en.has(w)) en += 1;
  }
  if (es >= 2 && es >= en * 2) return 'es';
  if (en >= 2 && en >= es * 2) return 'en';
  if (es === 1 && en === 0) return 'es';
  if (en === 1 && es === 0) return 'en';
  return null;
}


/** The translatable text fields per table (the same fields each surface renders). */
export const TRANSLATABLE_FIELDS = Object.freeze({
  quote_lines: Object.freeze(['title_override', 'subtitle_override', 'description_override', 'product_name', 'description']),
  quote_sections: Object.freeze(['name']),
  project_bundles: Object.freeze(['title', 'description']),
  // TICKET-977 S1b: a bundle item is its own row (stable id) — an ordinary translatable row.
  project_bundle_items: Object.freeze(['description']),
  quotes: Object.freeze(['cover_title', 'personalized_message', 'payment_terms', 'discount_label']),
  // TICKET-959: production content after WON. Raw transcripts and BOM material lines
  // are deliberately NOT here (spoken words / product + SKU names stay as they are).
  tasks: Object.freeze(['task_name', 'notes']),
  notes: Object.freeze(['body']),
  task_comments: Object.freeze(['body']),
  voice_records: Object.freeze(['headline', 'summary', 'body']),
  project_documents: Object.freeze(['summary']),
  // TICKET-1471: voice action cards before accept (source = the conversation language).
  derived_actions: Object.freeze(['title', 'description']),
});

/**
 * TICKET-1458: tables whose text is written by the AI in a KNOWN language. Detection is
 * for human text; an English photo description that quotes Spanish trade terms
 * ("... upholstery / cojines de bañera ...") reads as Spanish to the detector and was
 * never translated (quote #111).
 */
export const AI_SOURCE_LANG = Object.freeze({ project_documents: 'en' });

/**
 * `row` with its known source language stamped when it has no stamp yet. PURE.
 * TICKET-1464: since TICKET-1404 the describe drain writes a photo summary in the
 * capture language (`document_metadata.language`), so that wins over the 'en' default.
 */
export function withKnownSource(row, table) {
  const fallback = AI_SOURCE_LANG[table];
  if (!row || !fallback || TRANSLATION_LANGS.includes(row.translations?._source_lang)) return row;
  const meta = row.document_metadata?.language;
  const src = TRANSLATION_LANGS.includes(meta) ? meta : fallback;
  return { ...row, translations: { ...(row.translations || {}), _source_lang: src } };
}

/** How many replaced human-written versions a row keeps (TICKET-959: never lose what someone typed). */
export const HISTORY_CAP = 20;

function nonEmpty(v) {
  return typeof v === 'string' && v.trim() !== '';
}

/** FNV-1a (32-bit, hex) over the row's non-empty source fields, field-name keyed so a
 *  moved value changes it. Pure JS: this module also runs in the browser (builder). */
export function sourceHash(row, fields) {
  const parts = [];
  for (const f of fields) if (nonEmpty(row?.[f])) parts.push(`${f}=${row[f]}`);
  let h = 0x811c9dc5;
  for (const ch of parts.join('\u0001')) {
    h ^= ch.codePointAt(0);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/**
 * The row's source language: the stamp when it has been translated before, else the
 * language its own text reads as (TICKET-933 cycle 1: Aivan's bundles were written in
 * Spanish on an English quote — assuming the quote language stored English as 'es'),
 * else the quote's language.
 */
export function sourceLang(row, quoteLang, fields) {
  const s = row?.translations?._source_lang;
  if (TRANSLATION_LANGS.includes(s)) return s;
  if (fields) {
    const detected = detectTextLanguage(fields.map((f) => row?.[f]).filter(nonEmpty).join(' '));
    if (detected) return detected;
  }
  return quoteLang === 'en' ? 'en' : 'es';
}

/** True when `lang` needs (re)translation: source has text and the copy is missing or stale. */
export function needsTranslation(row, fields, lang, quoteLang) {
  if (!TRANSLATION_LANGS.includes(lang)) return false;
  if (lang === sourceLang(row, quoteLang, fields)) return false;
  if (!fields.some((f) => nonEmpty(row?.[f]))) return false;
  const copy = row?.translations?.[lang];
  return !copy || copy._src !== sourceHash(row, fields);
}

/**
 * Text of `field` in `lang`. The source language, a missing copy or a stale copy
 * all fall back to the source text — a render never shows a blank or an outdated
 * translation.
 */
export function pickText(row, field, lang, { fields, quoteLang } = {}) {
  const src = row?.[field];
  if (!TRANSLATION_LANGS.includes(lang) || lang === sourceLang(row, quoteLang, fields)) return src;
  const copy = row?.translations?.[lang];
  if (!copy || !nonEmpty(copy[field])) return src;
  if (fields && copy._src !== sourceHash(row, fields)) return src;
  return copy[field];
}

/** Localised copy of a row: every translatable field replaced by pickText. */
export function localizeRow(row, table, lang, quoteLang) {
  const fields = TRANSLATABLE_FIELDS[table];
  if (!row || !fields) return row;
  const out = { ...row };
  for (const f of fields) out[f] = pickText(row, f, lang, { fields, quoteLang });
  return out;
}

/** The `translations` value to store after translating `row` into `lang`. */
export function withTranslation(row, fields, lang, translated, quoteLang) {
  const copy = { _src: sourceHash(row, fields), _at: new Date().toISOString() };
  for (const f of fields) if (nonEmpty(translated?.[f])) copy[f] = translated[f];
  const prev = row?.translations || {};
  return { ...prev, _source_lang: sourceLang(row, quoteLang, fields), [lang]: copy };
}

/**
 * TICKET-959: the language actually on screen for `row` when the viewer asked for
 * `lang` — `lang` only when a FRESH copy exists, else the source language (the
 * screen is showing the source text as a fallback). The edit rule keys on this, so
 * an edit made over fallback text is never mistaken for an edit "in the other language".
 */
export function shownLanguage(row, fields, lang, quoteLang) {
  const src = sourceLang(row, quoteLang, fields);
  if (!TRANSLATION_LANGS.includes(lang) || lang === src) return src;
  const copy = row?.translations?.[lang];
  return copy && copy._src === sourceHash(row, fields) ? lang : src;
}

/**
 * TICKET-959 edit rule (CEO 2026-09-27): an edit made in the language on screen
 * becomes the new SOURCE; the other language is re-translated from it. The replaced
 * human-written text is KEPT in translations._history (who / when / language), capped
 * at HISTORY_CAP, so nothing anyone typed is ever lost.
 *
 * Returns { patch, translations }: `patch` = the source columns to write, `translations`
 * = the full new translations value. Write both in ONE update. PURE.
 *
 * @param {object} row       the stored row (source columns + translations)
 * @param {string[]} fields  TRANSLATABLE_FIELDS[table]
 * @param {string} lang      the language the viewer is looking at
 * @param {object} edits     { field: newText } for the fields they changed
 * @param {{ quoteLang?: string, userId?: string|null, now?: string }} [opts]
 */
export function editInLanguage(row, fields, lang, edits, { quoteLang, userId = null, now } = {}) {
  const at = now || new Date().toISOString();
  const changed = {};
  for (const f of fields) if (edits && Object.prototype.hasOwnProperty.call(edits, f)) changed[f] = edits[f];
  const prev = row?.translations || {};
  const src = sourceLang(row, quoteLang, fields);
  const shown = shownLanguage(row, fields, lang, quoteLang);

  // Same-language edit (or an edit over fallback source text): a normal edit. The other
  // language's copy goes stale by hash and is redone on next use. Stamp the source
  // language so a short edit can't later be misread by the detector.
  if (shown === src) {
    return { patch: changed, translations: { ...prev, _source_lang: src } };
  }

  // Cross-language edit: the shown language becomes the source. Unedited fields take
  // the fresh copy's text, so the row never ends up half in one language.
  const copy = prev[shown] || {};
  const patch = {};
  for (const f of fields) {
    if (Object.prototype.hasOwnProperty.call(changed, f)) patch[f] = changed[f];
    else if (nonEmpty(copy[f])) patch[f] = copy[f];
    else patch[f] = row?.[f] ?? null;
  }
  const replaced = {};
  for (const f of fields) if (nonEmpty(row?.[f])) replaced[f] = row[f];
  const history = [...(Array.isArray(prev._history) ? prev._history : []),
    { at, by: userId, lang: src, fields: replaced }].slice(-HISTORY_CAP);

  // The old source text becomes the other language's copy, stamped with the OLD hash:
  // it no longer matches the new source, so it reads as stale and is re-translated.
  const oldCopy = { ...replaced, _src: sourceHash(row, fields), _at: at };
  return { patch, translations: { _source_lang: shown, _history: history, [src]: oldCopy } };
}
